import { apiErrorMessage, isForbidden } from '@agos/api-client'
import { useGetEmployees, useGetEmployeesDrivers } from '@agos/api-client/hr'
import { postFiskalPrintNote, postFiskalReceipt } from '@agos/api-client/fiskal'
import {
  getGetOrdersQueryKey,
  postOrdersBulkDispatch,
  postOrdersOrderIdConfirmStatus,
  useGetOrders,
  useGetSalesChannels,
  usePatchOrdersOrderId,
} from '@agos/api-client/orders'
import {
  alreadyPrinted,
  chimeFor,
  chimeToggleLabel,
  filterChannelsOf,
  formatCents,
  formatMoney,
  startOfDay,
  T,
  tt,
  orderStatusLabel,
  useArrivalChime,
  useAuth,
  usesDrivers,
} from '@agos/shared'
import {
  CancelOrderModal,
  BUTTON_GHOST,
  cn,
  EmptyState,
  PageHeader,
  StatusBadge,
  toast,
} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import {
  Ban,
  Banknote,
  CalendarClock,
  Check,
  Clock,
  CreditCard,
  MapPin,
  Pencil,
  Phone,
  Printer,
  Receipt,
  RefreshCw,
  Send,
  ShoppingBag,
  Store,
  Truck,
  User,
  UserCheck,
  Volume2,
  VolumeX,
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'wouter'

import { mayAssign, orderIdsToWrite, outcomeNote, planFor, planNote } from './bulk-assign'
import {
  askNote,
  assignPayload,
  assignableWaiters,
  closeReasonFor,
  mustAskWhoServed,
  noWaitersNote,
  unattributedNote,
  type WaiterLike,
} from './waiter-assign'

import {
  arrivalsSince,
  PREORDER_BAND,
  boardBands,
  boardOrders,
  driverLoads,
  orderIdsOf,
  outsideEveryZone,
  credentialsNeeded,
  protectedStatusPlan,
  statusChangeConfirmLabel,
  statusChangeSubtitle,
  statusChangeTitle,
  statusRoleNote,
  visibleOrders,
  type BoardOrder,
} from './board'
import {
  printNotePayload,
  receiptPayloadFor,
  receiptOutcomeNote,
  printOutcomeNote,
} from './delivery-note'
import {
  canReassignDriver,
  driverControlFor,
  dispatchRuns,
  isDispatchable,
  mayCloseFromBoard,
  mayEditFromBoard,
  arrivalLabel,
  channelBadge,
  discountParts,
  elapsedLabel,
  paymentBadge,
  paymentMethodBadge,
  scheduledBadge,
  sourceBadge,
  verificationOf,
  nextStatusLabel,
  nextStatusesFor,
} from './dispatch-rules'

/**
 * `/dispatch` — *Rozvoz — nástenka*, ported from legacy `pages/dispatch.tsx`.
 *
 * The road: everything not yet delivered, who is carrying it, and the one action
 * that matters — sending a driver out with their run.
 *
 * §14.1 — ten seconds, copied. The countdown ring is here for the same reason it
 * is on the kitchen board: a dispatcher who cannot see the tick has no way to
 * tell a quiet evening from a screen that stopped fetching.
 */

const REFRESH_SECONDS = 10
const ORDERS_PAGE_SIZE = '500'

const TYPE_ICONS: Record<string, typeof Truck> = {
  delivery: Truck,
  restaurant: Store,
  bar: ShoppingBag,
}

const STATUS_TABS = [
  // First, as the owner asked — §9.9. `active` stays selected on open, because that is the working
  // view; this is the tab you reach for when an order has gone somewhere you cannot see.
  { id: 'all', label: tt('Všetko') },
  { id: 'active', label: tt('Aktívne') },
  { id: 'pending', label: tt('Čaká na prijatie') },
  { id: 'new', label: tt('Nová') },
  { id: 'preparing', label: tt('Príprava') },
  { id: 'quality_check', label: tt('Kontrola kvality') },
  { id: 'on_the_way', label: tt('Na ceste') },
  { id: 'delivered', label: tt('Doručená') },
] as const

interface DriverLike {
  id?: number
  name?: string
}

/**
 * §9.149 — legacy's *"left strip color and row background tint per status"*, ported from
 * `dispatch.tsx`'s own `STATUS_STRIP` and `STATUS_TINT`.
 *
 * **Local to this board on purpose.** `manager-orders` has a badge palette for the same statuses and
 * it disagrees — `on_the_way` is violet there and near-black here — because they are different
 * pictures: a badge names a state, a strip lets an eye landing mid-list see which row it is on. A
 * shared map would have to pick one and be wrong on the other screen.
 */
const STATUS_STRIP: Record<string, string> = {
  pending: 'bg-red-500',
  new: 'bg-green-500',
  preparing: 'bg-violet-500',
  ready: 'bg-orange-500',
  quality_check: 'bg-blue-500',
  on_the_way: 'bg-zinc-900',
  at_address: 'bg-orange-500',
  delivered: 'bg-emerald-500',
  cancelled: 'bg-zinc-400',
}

const STATUS_TINT: Record<string, string> = {
  pending: 'bg-red-500/5',
  new: 'bg-green-500/5',
  preparing: 'bg-violet-500/5',
  ready: 'bg-orange-500/5',
  quality_check: 'bg-blue-500/5',
  on_the_way: 'bg-zinc-900/5',
  at_address: 'bg-orange-500/5',
  delivered: 'bg-emerald-500/5',
  cancelled: 'bg-zinc-400/5',
}

/** A status this board has never heard of keeps the plain card rather than losing the row. */
function statusStrip(status: string | undefined): string {
  return STATUS_STRIP[status ?? ''] ?? 'bg-transparent'
}

function statusTint(status: string | undefined): string {
  return STATUS_TINT[status ?? ''] ?? 'bg-card'
}

export function DispatchPage() {
  const queryClient = useQueryClient()
  const [, navigate] = useLocation()
  const { hasPermission, user } = useAuth()

  const [filterStatus, setFilterStatus] = useState<string>('active')
  const [selected, setSelected] = useState<ReadonlySet<number>>(new Set())
  const [secondsToRefresh, setSecondsToRefresh] = useState(REFRESH_SECONDS)
  const [isDispatching, setIsDispatching] = useState(false)
  const [isPrinting, setIsPrinting] = useState(false)
  const [isPrintingFiskal, setIsPrintingFiskal] = useState(false)
  const chime = useArrivalChime()
  const [bulkDriverId, setBulkDriverId] = useState<number | null>(null)
  const { data: employeeData } = useGetEmployees()
  const waiters = useMemo(
    () => assignableWaiters(Array.isArray(employeeData) ? (employeeData as WaiterLike[]) : []),
    [employeeData],
  )
  const [isAssigning, setIsAssigning] = useState(false)
  const [settling, setSettling] = useState<{ order: BoardOrder; status: string } | null>(null)
  const [askingWho, setAskingWho] = useState<BoardOrder | null>(null)
  const [now, setNow] = useState(() => new Date())
  const seenOrderIds = useRef<Set<number> | null>(null)

  const today = startOfDay(new Date()).toISOString()
  const { data: result, isLoading } = useGetOrders(
    { date_from: today, page_size: ORDERS_PAGE_SIZE },
    { query: { refetchInterval: REFRESH_SECONDS * 1000 } },
  )
  const allOrders = useMemo(() => (result?.data ?? []) as BoardOrder[], [result])

  const { data: driverData } = useGetEmployeesDrivers({
    query: { refetchInterval: REFRESH_SECONDS * 1000 },
  })
  const drivers = useMemo(
    () => (Array.isArray(driverData) ? (driverData as DriverLike[]) : []),
    [driverData],
  )

  const patchOrder = usePatchOrdersOrderId()

  /**
   * The ring and the clock, one tick. It does not fetch — FB-046 again: the
   * query owns its interval, and a second invalidation on the same schedule
   * would double the request rate.
   */
  useEffect(() => {
    const tick = setInterval(() => {
      setNow(new Date())
      setSecondsToRefresh((remaining) => (remaining <= 1 ? REFRESH_SECONDS : remaining - 1))
    }, 1000)
    return () => clearInterval(tick)
  }, [])

  // §9.5: the board asks what a channel does. Required rather than optional, so it cannot be forgotten.
  const { data: salesChannels } = useGetSalesChannels()
  const channels = useMemo(() => filterChannelsOf(salesChannels), [salesChannels])
  const onBoard = useMemo(() => boardOrders(allOrders, channels), [allOrders, channels])
  const shown = useMemo(
    () => visibleOrders(allOrders, filterStatus, channels),
    [allOrders, filterStatus, channels],
  )
  const loads = useMemo(() => driverLoads(onBoard), [onBoard])

  /** A web order nobody has looked at is announced differently from an accepted one. */
  useEffect(() => {
    if (allOrders.length === 0) return
    const notice = arrivalsSince(allOrders, seenOrderIds.current)
    seenOrderIds.current = orderIdsOf(allOrders)

    // §9.110 — the same arrival that raises the toast makes the sound, so nothing fires on a
    // re-render and the urgent pattern goes to the order nobody has accepted.
    const kind = chimeFor({ pending: notice.pending.length, accepted: notice.accepted.length })
    if (kind !== null) chime.play(kind)

    if (notice.pending.length > 0) {
      toast({
        title: `⚠️ ${notice.pending.length === 1 ? tt('Nová objednávka z webu!') : `${notice.pending.length} ${tt('nové objednávky z webu!')}`}`,
        description: notice.pending
          .map((order) => `#${order.orderNumber} - ${formatMoney(order.total)}`)
          .join('  |  '),
      })
    }
    if (notice.accepted.length > 0) {
      toast({
        title: `🔔 ${notice.accepted.length === 1 ? tt('Nová objednávka') : `${notice.accepted.length} ${tt('nové objednávky')}`}!`,
        description: notice.accepted
          .map((order) => `#${order.orderNumber} - ${formatMoney(order.total)}`)
          .join('  |  '),
      })
    }
  }, [allOrders, chime])

  /**
   * One slip per selected order — §9.108.
   *
   * <p>Sequential, and a failure does not stop the rest: the register is a single serial device, and
   * the orders that can be printed should be. Each failure is named in the toast rather than folded
   * into a count, because a dispatcher needs to know *which* driver is about to leave without one.
   */
  const printNotesForSelected = async () => {
    if (selectedOrders.length === 0) return
    setIsPrinting(true)
    let printed = 0
    const failed: string[] = []
    for (const order of selectedOrders) {
      try {
        await postFiskalPrintNote(printNotePayload(order))
        printed += 1
      } catch {
        failed.push(`#${order.orderNumber ?? order.id ?? '?'}`)
      }
    }
    setIsPrinting(false)
    toast({
      ...(failed.length === 0 ? {} : { variant: 'destructive' as const }),
      title: printOutcomeNote(printed, failed),
    })
  }

  /**
   * §9.123 — the bulk fiscal print legacy has on this bar and AGOS did not.
   *
   * **Sequential, one order at a time.** A register is a single serial device and firing eight
   * requests at it in parallel is how two documents interleave. **A failure stops nothing else** —
   * one unreachable register must not hold up the other seven — and every failure is named by order
   * number, because an unissued receipt is a sale with no tax document and somebody has to chase it.
   *
   * The delivery notes follow **only the orders that printed**: a note for a bag whose receipt failed
   * would send a driver out with an undocumented sale.
   */
  const printFiskalForSelected = async () => {
    if (selectedOrders.length === 0) return
    setIsPrintingFiskal(true)
    const issued: typeof selectedOrders = []
    const failed: string[] = []
    /**
     * §9.362, FB-649 - the orders whose document the server says **already exists.**
     *
     * They used to land in `failed`, so a batch of ten with three already printed reported three
     * failures and sent the dispatcher after receipts that were on the tray.
     *
     * **The orders themselves, not their labels, because they still want a slip.** The rule the
     * slip loop below was written to - *the bag whose receipt failed must not get a slip, or a
     * driver leaves with an undocumented sale* - reads the other way round here: this sale **is**
     * documented, so withholding the slip would send a driver out without one for a receipt that
     * exists.
     */
    const alreadyHad: typeof selectedOrders = []
    for (const order of selectedOrders) {
      const payload = receiptPayloadFor(order)
      if (payload === null) {
        // A tax document must say how the sale was paid, and this one does not know. Guessing would
        // put a wrong payment method on a real document, so it is named instead.
        failed.push(`#${order.orderNumber ?? order.id ?? '?'}`)
        continue
      }
      try {
        await postFiskalReceipt(payload)
        issued.push(order)
      } catch (caught) {
        if (alreadyPrinted(caught)) alreadyHad.push(order)
        else failed.push(`#${order.orderNumber ?? order.id ?? '?'}`)
      }
    }
    setIsPrintingFiskal(false)
    toast({
      ...(failed.length === 0 ? {} : { variant: 'destructive' as const }),
      title: receiptOutcomeNote(
        issued.length,
        failed,
        alreadyHad.map((order) => `#${order.orderNumber ?? order.id ?? '?'}`),
      ),
    })
    // A documented sale gets its slip, whether this batch printed the document or found it already
    // there - §9.362.
    for (const order of [...issued, ...alreadyHad]) {
      try {
        await postFiskalPrintNote(printNotePayload(order))
      } catch {
        // The receipt is the document that matters and it is already issued; a missing slip is a
        // reprint, not a lost sale.
      }
    }
  }

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: getGetOrdersQueryKey() })
    setSecondsToRefresh(REFRESH_SECONDS)
  }

  const toggleSelected = (orderId: number) =>
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(orderId)) next.delete(orderId)
      else next.add(orderId)
      return next
    })

  const selectedOrders = shown.filter((order) => selected.has(order.id ?? 0))
  const runs = dispatchRuns(selectedOrders)
  const bulkPlan = bulkDriverId === null ? null : planFor(selectedOrders, bulkDriverId)
  const bulkDriverName =
    drivers.find((driver) => driver.id === bulkDriverId)?.name ?? tt('šofér')

  /**
   * N patches, because assignment is per order — `bulk-dispatch` moves already-assigned orders and is a
   * different action. A failure does not stop the rest: the bags that can go, go.
   */
  const assignSelected = async () => {
    if (bulkPlan === null || !mayAssign(bulkPlan) || bulkDriverId === null) return
    setIsAssigning(true)
    let written = 0
    let failed = 0
    for (const orderId of orderIdsToWrite(bulkPlan)) {
      try {
        await patchOrder.mutateAsync({ orderId, data: { driverId: bulkDriverId } })
        written += 1
      } catch {
        failed += 1
      }
    }
    setIsAssigning(false)
    refresh()
    toast({
      ...(failed === 0 ? {} : { variant: 'destructive' as const }),
      title: outcomeNote(written, failed),
    })
  }

  const assignDriver = (order: BoardOrder, driverId: number) => {
    if (!canReassignDriver(order)) {
      // §14.5 — the credit belongs to the journey somebody is making.
      toast({
        variant: 'destructive',
        title: tt('Šofér je na ceste'),
        description: tt('Objednávku musí vrátiť šofér cez mobilnú aplikáciu.'),
      })
      return
    }
    patchOrder.mutate(
      { orderId: order.id ?? 0, data: { driverId } },
      {
        onSuccess: refresh,
        onError: (caught) =>
          toast({
            variant: 'destructive',
            title: tt('Priradenie zlyhalo'),
            description: apiErrorMessage(caught, ''),
          }),
      },
    )
  }

  const changeStatus = (order: BoardOrder, status: string) => {
    // FB-075: a bar bill nobody is on goes into the takings and into no wallet. The one moment somebody
    // knows who served it is now, while it is being closed.
    // §9.391 - the channel decides who owns the money, so `na_sekeru` is asked too.
    if (status === 'delivered' && mustAskWhoServed(order, channels)) {
      setAskingWho(order)
      return
    }

    // §9.341, FB-547 - the owner: *"when I want to storno some orders, I rredceive error … Použite
    // /confirm-status"*. `OrderUpdateService` refuses all three settling statuses on the ordinary
    // PATCH, so the board could not storno at all and printed the server's developer-facing guard
    // into a toast.
    //
    // **No credentials, and no modal.** `OrderActorResolver.resolve` falls back to the signed-in
    // principal when e-mail and password are absent, so the actor, the role gate and the status log
    // are all the same as they would be behind a prompt - and asking a signed-in administrator for
    // the password they signed in with proves nothing the token has already proved.
    if (protectedStatusPlan(status) === 'confirm') {
      // The reason is collected, always: `reason` is `@NotBlank` and a default would put a fiction
      // in the status log. The credentials are collected only when this session cannot authorise
      // the change - `credentialsNeeded`, which is the owner's complaint in one function.
      setSettling({ order, status })
      return
    }

    patchOrder.mutate(
      { orderId: order.id ?? 0, data: { status } },
      {
        onSuccess: refresh,
        onError: (caught) =>
          toast({
            variant: 'destructive',
            title: tt('Nepodarilo sa zmeniť stav.'),
            description: apiErrorMessage(caught, ''),
          }),
      },
    )
  }

  /**
   * One request per driver, because a driver leaving with three bags is one
   * journey. Failures are reported per run and the others still go — a dispatcher
   * mid-rush needs the drivers that *can* leave to leave.
   */
  /** §9.341 - the order and status waiting on a reason (and maybe an administrator). */
  const confirmSettling = async (email: string, password: string, reason: string) => {
    if (settling === null) return
    const { order, status } = settling
    try {
      await postOrdersOrderIdConfirmStatus(order.id ?? 0, {
        newStatus: status,
        reason,
        // Sent only when collected. **Empty strings would be worse than absent**:
        // `OrderActorResolver.resolve` checks `email != null && password != null`, so a blank pair
        // would fall to `byCredentials` and fail on a password nobody typed, instead of falling back
        // to the signed-in principal.
        ...(email.trim() === '' ? {} : { email: email.trim(), password }),
      })
      setSettling(null)
      refresh()
      toast({ title: tt('Stav zmenený'), description: `${tt('Dôvod')}: ${reason}` })
    } catch (caught) {
      // Rethrown so the modal shows it in place and keeps what was typed - and a 403 is said as who
      // *may* do it, because the server's own sentence for the PATCH guard names an endpoint.
      throw new Error(
        isForbidden(caught) ? statusRoleNote(status) : apiErrorMessage(caught, ''),
        { cause: caught },
      )
    }
  }

  const dispatchSelected = async () => {
    if (runs.length === 0) {
      toast({
        variant: 'destructive',
        title: tt('Žiadne vhodné objednávky'),
        description: tt(
          'Vybrané objednávky musia byť v stave Kontrola kvality s priradeným šoférom.',
        ),
      })
      return
    }

    setIsDispatching(true)
    let sent = 0
    for (const run of runs) {
      try {
        // `dispatched` is typed now, so neither the cast nor the runtime check earns its place.
        const response = await postOrdersBulkDispatch({
          orderIds: run.orderIds,
          driverId: run.driverId,
        })
        sent += response.dispatched
      } catch (caught) {
        toast({
          variant: 'destructive',
          title: tt('Chyba dispatchu'),
          description: apiErrorMessage(caught, tt('Nepodarilo sa odoslať objednávky.')),
        })
      }
    }

    refresh()
    setIsDispatching(false)
    if (sent > 0) {
      const eligible = selectedOrders.filter(isDispatchable).length
      toast({
        title: `${tt('Vyrazené')} (${sent}/${eligible})`,
        description: tt('Objednávky odoslané na cestu.'),
      })
      setSelected(new Set())
    }
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title={tt('Rozvoz - nástenka')}
        icon={<Truck className="w-5 h-5" />}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <span
              className="text-[11px] text-muted-foreground tabular-nums"
              title={tt('Obnovuje sa automaticky každých 10s.')}
              aria-live="polite"
            >
              {secondsToRefresh}s
            </span>
            {/* §9.110 - off by default, and the press is also the gesture the browser needs before
              * an AudioContext will start. Legacy has this on the same toolbar. */}
            <button
              type="button"
              onClick={chime.toggle}
              aria-label={chimeToggleLabel(chime.enabled)}
              data-sound-toggle={chime.enabled ? 'on' : 'off'}
              className={cn(BUTTON_GHOST, 'p-2 rounded-lg')}
            >
              {chime.enabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </button>
            <button
              type="button"
              onClick={refresh}
              aria-label={tt('Obnoviť')}
              className={cn(BUTTON_GHOST, 'p-2 rounded-lg')}
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        }
      />

      {loads.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {loads.map((load) => (
            <div
              key={load.driverId}
              className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-violet-500/10 border border-violet-500/25 text-xs"
            >
              <User className="w-3.5 h-3.5 text-violet-500" />
              <span className="font-bold">{load.name}</span>
              <span className="text-muted-foreground">
                {load.orderCount} · {formatCents(load.totalCents)}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-1.5">
        {STATUS_TABS.map((tab) => {
          // `all` and `active` are not statuses, so counting rows whose status equals the tab id gives
          // them nothing — *Všetko* would have shown up empty beside six tabs with numbers.
          const count =
            tab.id === 'all'
              ? onBoard.length
              : tab.id === 'active'
                ? visibleOrders(allOrders, 'active', channels).length
                : onBoard.filter((order) => order.status === tab.id).length
          return (
            <button
              key={tab.id}
              type="button"
              data-status-tab={tab.id}
              onClick={() => setFilterStatus(tab.id)}
              className={cn(
                'px-3 py-1.5 rounded-lg text-xs font-bold transition-colors',
                filterStatus === tab.id
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted hover:bg-muted/70 text-muted-foreground',
              )}
            >
              {tab.label} {count > 0 && <span className="tabular-nums">({count})</span>}
            </button>
          )
        })}
      </div>

      {/* §9.150 - **always visible, as legacy's is.** It rendered only once something was selected,
        * so a freshly loaded board showed filter chips and nothing else: every action this screen
        * exists for was invisible until you had already done the one thing whose control was also
        * invisible. Each button is disabled on an empty selection and says so, which is §9.148's
        * rule — an affordance that explains its refusal beats one that is absent. */}
      <div
        className="flex flex-wrap items-center gap-2 px-3 py-2.5 rounded-xl bg-card border border-border"
        data-dispatch-actions
      >
          <span className="text-xs font-bold">
            {selected.size > 0
              ? `${selected.size} ${tt('vybraných')}`
              : tt('Vyberte objednávky')}
          </span>
          <span className="text-[11px] text-muted-foreground">
            {selected.size === 0
              ? tt('označte riadky vľavo')
              : runs.length > 0
                ? `${runs.length} ${runs.length === 1 ? tt('jazda') : tt('jazdy')}`
                : tt('nič na vyrazenie')}
          </span>
          {bulkPlan !== null && (
            <span className="text-[11px] text-muted-foreground" data-bulk-plan>
              {planNote(bulkPlan, bulkDriverName)}
            </span>
          )}
          <div className="flex-1" />
          {/* §9.154 - legacy's *Vybrať všetky*, which we had dropped. Printing forty delivery notes
            * is the flow this bar exists for, and forty clicks to get there is not that flow. It
            * selects what is on screen, not what exists: a filtered board selects the filter. */}
          <button
            type="button"
            data-select-all
            onClick={() =>
              setSelected((current) =>
                current.size === shown.length && shown.length > 0
                  ? new Set()
                  : new Set(shown.map((order) => order.id).filter((id): id is number => id !== undefined)),
              )
            }
            disabled={shown.length === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs font-semibold hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Check className="w-3.5 h-3.5" />
            {selected.size === shown.length && shown.length > 0 ? (
              <T>Zrušiť všetky</T>
            ) : (
              <T>Vybrať všetky</T>
            )}
          </button>
          {/* §9.123 - legacy's order, left to right: print the slips, print the receipts, then
            * assign, then dispatch, with the cancel pushed to the far right where it cannot be hit
            * by accident. */}
          <button
            type="button"
            onClick={() => void printNotesForSelected()}
            disabled={isPrinting || selectedOrders.length === 0}
            data-print-notes
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs font-semibold hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Printer className="w-3.5 h-3.5" /> <T>Tlačiť sprievodný list</T>
          </button>
          <button
            type="button"
            onClick={() => void printFiskalForSelected()}
            disabled={isPrintingFiskal || selectedOrders.length === 0}
            data-print-fiskal
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-700 text-white text-xs font-bold hover:bg-emerald-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Receipt className="w-3.5 h-3.5" />{' '}
            {isPrintingFiskal ? <T>Odosielam…</T> : <T>Tlačiť fiškálny doklad</T>}
          </button>
          {/* Assign the selection to one driver - three bags going the same way, named once. */}
          <select
            value={bulkDriverId === null ? '' : String(bulkDriverId)}
            onChange={(event) =>
              setBulkDriverId(event.target.value === '' ? null : Number(event.target.value))
            }
            aria-label={tt('Priradiť šoférovi')}
            data-bulk-driver
            className="min-h-[32px] rounded-lg bg-card border border-border px-2 text-xs"
          >
            <option value="">{tt('- šofér -')}</option>
            {drivers.map((driver) => (
              <option key={driver.id} value={driver.id}>
                {driver.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            data-bulk-assign
            onClick={() => void assignSelected()}
            disabled={isAssigning || bulkPlan === null || !mayAssign(bulkPlan)}
            className="px-3 py-1.5 rounded-lg border border-border text-xs font-bold hover:bg-muted transition-colors disabled:opacity-50"
          >
            <T>Priradiť</T>
          </button>
          <button
            type="button"
            onClick={() => void dispatchSelected()}
            disabled={isDispatching || runs.length === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500 text-white text-xs font-bold hover:bg-emerald-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="w-3.5 h-3.5" /> <T>Vyraziť</T>
          </button>
          {/* Last, and away from the actions - legacy puts it here for the same reason. */}
          <button
            type="button"
            data-clear-selection
            onClick={() => setSelected(new Set())}
            className="px-2.5 py-1.5 rounded-lg border border-border text-xs font-semibold text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <T>Zrušiť výber</T>
          </button>
        </div>

      {unattributedNote(shown, channels) !== '' && (
        <p className="text-[11px] text-amber-500 px-1" data-unattributed>
          {unattributedNote(shown, channels)}
        </p>
      )}

      {/* §9.341, FB-547 - the board had no modal at all, so a storno hit the server's PATCH guard
        * and printed *"Použite /confirm-status"* to the dispatcher. The reason is always asked; the
        * credentials only when this session cannot authorise the change. */}
      {settling !== null && (
        <div data-settling={settling.status}>
          <CancelOrderModal
            order={settling.order}
            askCredentials={credentialsNeeded(user?.systemRole ?? '', settling.status)}
            subtitle={statusChangeSubtitle(settling.order, settling.status)}
            // §9.409 - the heading and the button say which of the three this is. The subtitle has
            // named it since §9.341; these two were hard-coded to the cancel wording, so pressing
            // *Vydaná* opened a dialog headed *Stornovať objednávku*.
            title={statusChangeTitle(settling.status)}
            confirmLabel={statusChangeConfirmLabel(settling.status)}
            onClose={() => setSettling(null)}
            onConfirm={confirmSettling}
          />
        </div>
      )}

      {askingWho !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-who-served>
          <button
            type="button"
            className="absolute inset-0 bg-black/60"
            aria-label={tt('Zavrieť')}
            onClick={() => setAskingWho(null)}
          />
          <section className="relative w-full max-w-sm rounded-2xl bg-background border border-border p-5 space-y-3">
            <h2 className="text-sm font-bold">
              <T>Kto obsluhoval?</T>
            </h2>
            <p className="text-[11px] text-muted-foreground" data-who-served-note>
              {askNote(askingWho)}
            </p>
            {waiters.length === 0 ? (
              <p className="text-[11px] text-amber-500" data-no-waiters>
                {noWaitersNote()}
              </p>
            ) : (
              <div className="space-y-1.5">
                {waiters.map((waiter) => (
                  <button
                    key={waiter.id}
                    type="button"
                    data-pick-waiter={waiter.id}
                    onClick={() => {
                      const target = askingWho
                      setAskingWho(null)
                      // §9.342 - two writes, and the second used to be the plain `PATCH` with a
                      // settling status: the call `OrderUpdateService.guard` refuses, with **no
                      // `onError`**. So the waiter was assigned, the close failed, and nothing was
                      // said. It goes through `confirm-status` now, like every other settling status
                      // on this board since §9.341, and both halves report.
                      void (async () => {
                        try {
                          await patchOrder.mutateAsync({
                            orderId: target.id ?? 0,
                            data: assignPayload(waiter.id ?? 0),
                          })
                        } catch (caught) {
                          toast({
                            variant: 'destructive',
                            title: tt('Nepodarilo sa priradiť obsluhu.'),
                            description: apiErrorMessage(caught, ''),
                          })
                          return
                        }
                        try {
                          // The reason is the waiter just picked - the datum this dialog exists to
                          // collect, not a default invented to satisfy `@NotBlank`.
                          await postOrdersOrderIdConfirmStatus(target.id ?? 0, {
                            newStatus: 'delivered',
                            reason: closeReasonFor(waiter),
                          })
                          refresh()
                        } catch (caught) {
                          // The missing `onError` was the defect underneath the defect, and the
                          // reason a silent failure survived a day of green runs.
                          toast({
                            variant: 'destructive',
                            title: tt('Obsluha je priradená, ale objednávku sa nepodarilo uzavrieť.'),
                            description: isForbidden(caught)
                              ? statusRoleNote('delivered')
                              : apiErrorMessage(caught, ''),
                          })
                          refresh()
                        }
                      })()
                    }}
                    className="w-full min-h-[44px] rounded-xl border border-border text-sm font-semibold hover:bg-muted"
                  >
                    {waiter.name}
                  </button>
                ))}
              </div>
            )}
          </section>
        </div>
      )}

      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3, 4].map((placeholder) => (
            <div
              key={placeholder}
              className="h-20 rounded-2xl bg-muted/40 border border-border animate-pulse"
            />
          ))}
        </div>
      ) : shown.length === 0 ? (
        <div className="bg-card border border-border rounded-2xl p-12">
          <EmptyState
            icon={<Truck className="w-10 h-10" />}
            title={tt('Žiadne objednávky')}
            description={tt('Nástenka je prázdna.')}
          />
        </div>
      ) : filterStatus === 'active' ? (
        /* The nástenka proper — §9.104. Grouped as legacy groups it, because a dispatcher picking one
         * status at a time can never see the road at once, and seeing the road at once is the point. */
        <div className="space-y-5" data-board-bands>
          {boardBands(shown, now).map((band) => (
            <div key={band.status} className="space-y-2" data-board-band={band.status}>
              <div
                className={cn(
                  'flex items-center gap-2 rounded-xl px-3 py-2 border',
                  // §9.318 - the pre-order band is marked so nobody reads it as today's work.
                  band.status === PREORDER_BAND
                    ? 'border-dashed border-amber-500/50 bg-amber-500/5'
                    : 'border-border bg-muted/30',
                )}
              >
                <span className={cn('w-2.5 h-2.5 rounded-full shrink-0', band.dot)} />
                <span className="text-xs font-black uppercase tracking-wider">{band.label}</span>
                {band.status === PREORDER_BAND && (
                  <span className="text-[11px] font-medium normal-case opacity-70">
                    <T>nie sú na dnes</T>
                  </span>
                )}
                <span className="ml-auto text-xs font-bold opacity-70" data-band-count>
                  {band.orders.length}
                </span>
              </div>
              <div className="space-y-2 pl-1">
                {band.orders.map((order) => (
                  <DispatchRow
                    key={order.id}
                    order={order}
                    clock={now}
                    drivers={drivers}
                    isSelected={selected.has(order.id ?? 0)}
                    canChangeStatus={hasPermission('change_order_status')}
                    canCancel={hasPermission('cancel_order')}
                    driven={usesDrivers(order.type ?? '', channels)}
                    channels={channels}
                    onToggleSelect={() => toggleSelected(order.id ?? 0)}
                    onAssignDriver={(driverId) => assignDriver(order, driverId)}
                    onStatusChange={(status) => changeStatus(order, status)}
                    onEdit={() => navigate(`/new-order?edit=${order.id ?? 0}`)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* Every other tab is already one status — a band round the whole list says nothing. */
        <div className="space-y-2">
          {shown.map((order) => (
            <DispatchRow
              key={order.id}
              order={order}
              clock={now}
              drivers={drivers}
              isSelected={selected.has(order.id ?? 0)}
              canChangeStatus={hasPermission('change_order_status')}
              canCancel={hasPermission('cancel_order')}
              driven={usesDrivers(order.type ?? '', channels)}
              channels={channels}
              onToggleSelect={() => toggleSelected(order.id ?? 0)}
              onAssignDriver={(driverId) => assignDriver(order, driverId)}
              onStatusChange={(status) => changeStatus(order, status)}
              onEdit={() => navigate(`/new-order?edit=${order.id ?? 0}`)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function DispatchRow({
  order,
  clock,
  drivers,
  isSelected,
  canChangeStatus,
  canCancel,
  driven,
  channels,
  onToggleSelect,
  onAssignDriver,
  onStatusChange,
  onEdit,
}: {
  order: BoardOrder
  clock: Date
  drivers: DriverLike[]
  isSelected: boolean
  canChangeStatus: boolean
  canCancel: boolean
  driven: boolean
  /** §9.391 - the row asks the channel whether this order is one somebody serves. */
  channels: readonly { slug?: string; onPremise?: boolean }[]
  onToggleSelect: () => void
  onAssignDriver: (driverId: number) => void
  onStatusChange: (status: string) => void
  onEdit: () => void
}) {
  const TypeIcon = TYPE_ICONS[order.type ?? ''] ?? Truck
  const transitions = nextStatusesFor(order.status).filter(
    (next) => (next === 'cancelled' ? canCancel : canChangeStatus),
  )
  const driverLocked = !canReassignDriver(order)
  // §9.323 - local to the card, and reset by the assignment itself below. Kept out of
  // `driverControlFor` because it is not a rule about the order; it is whether somebody has asked.
  const [reassigning, setReassigning] = useState(false)
  // §9.309, the owner's item B - what belongs where the picker used to be, unconditionally.
  const driverControl = driverControlFor(order, { driven, mayChange: canChangeStatus })
  // **The order's own name first, and that ordering is the point.** `GET /employees/drivers` answers
  // only drivers seen in the **last four hours**, so looking the name up there alone rendered an
  // empty badge for exactly the case this badge exists for: an order on the road whose driver has
  // since gone off duty. Caught by the e2e, which stubs `on_the_way` with a driver who is not on the
  // duty list. The id is the last resort, because *Šofér #7* is still more use than nothing.
  const driverName =
    order.driverName ??
    drivers.find((driver) => driver.id === order.driverId)?.name ??
    (order.driverId ? `${tt('Šofér')} #${order.driverId}` : '')
  const closesHere = canChangeStatus && mayCloseFromBoard(order, channels)
  const source = sourceBadge(order)
  const payment = paymentBadge(order)
  const phoneSeen = verificationOf(order.phoneOrderCount)
  // §9.154 — everything legacy's row says. `now` is taken once per render rather than inside each
  // helper, so the clock on one row cannot disagree with the clock on the row beneath it.
  const now = clock.getTime()
  const channel = channelBadge(order.type)
  const method = paymentMethodBadge(order.paymentMethod)
  // §9.204 — the same two icons the payment dialog uses, so the board and the till agree.
  const MethodIcon = method?.icon === 'cash' ? Banknote : CreditCard
  const arrived = arrivalLabel(order.createdAt, now)
  const waiting = elapsedLabel(order.createdAt, now)
  const discount = discountParts(order)
  const promised = scheduledBadge(order, now)
  const emailSeen = verificationOf(order.emailOrderCount)

  return (
    <div
      data-order={order.orderNumber}
      data-order-status={order.status ?? ''}
      className={cn(
        // §9.149 — the tint is what makes a status readable without reading the word.
        // §9.204 — shorter, so more orders fit under one another. The tints and the strip are
        // untouched: *farby zachovať* was explicit, and §9.149 records why they carry the status.
        'relative overflow-hidden border rounded-2xl pl-5 pr-4 py-2 flex flex-wrap items-center gap-2',
        statusTint(order.status),
        isSelected ? 'border-primary ring-1 ring-primary/30' : 'border-border',
      )}
    >
      {/* Legacy's left strip, the width it uses. */}
      <span
        aria-hidden
        data-status-strip={order.status ?? ''}
        className={cn('absolute left-0 top-0 bottom-0 w-1.5', statusStrip(order.status))}
      />
      <button
        type="button"
        aria-label={`${tt('Vybrať')} #${order.orderNumber}`}
        onClick={onToggleSelect}
        className={cn(
          'w-5 h-5 rounded-md border flex items-center justify-center flex-shrink-0 transition-colors',
          isSelected ? 'bg-primary border-primary text-primary-foreground' : 'border-border',
        )}
      >
        {isSelected && <Check className="w-3.5 h-3.5" />}
      </button>

      <div className="min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          {/* §9.204 - the thing a driver and a dispatcher say out loud to each other. It was
              `text-xs`: the size of the badges beside it and smaller than the customer name. */}
          <span className="font-mono text-xl font-black text-primary leading-none" data-order-number>
            #{order.orderNumber}
          </span>
          {/* §9.154 - legacy names the channel beside the icon. A truck and a bag are the same
            * silhouette at a glance, and *Osobný odber* is the one no driver should be assigned to. */}
          {channel !== null && (
            <span
              data-order-channel={order.type}
              className={cn(
                'flex items-center gap-0.5 text-[10px] font-bold rounded px-1 py-0.5 border leading-none',
                channel.tone,
              )}
            >
              <TypeIcon className="w-2.5 h-2.5" />
              {channel.label}
            </span>
          )}
          {method !== null && (
            <span
              data-payment-method={order.paymentMethod}
              className={cn(
                'flex items-center gap-0.5 text-[10px] font-bold rounded px-1 py-0.5 border leading-none',
                method.tone,
              )}
            >
              {/* §9.204 - §9.154 gave the channel an icon because *a truck and a bag are the same
                  silhouette at a glance*; cash and card are the two states somebody scans a whole
                  column for. The same two icons the payment dialog uses, so board and till agree. */}
              <MethodIcon className="w-2.5 h-2.5" />
              {method.label}
            </span>
          )}
        </div>
        {/* §9.125 - who the bag is for. This board decides which car it goes in and showed only the
          * driver, so the name, the number and the address were on every other screen but this one. */}
        <p className="text-sm font-semibold truncate">
          {order.customerName ?? order.driverName ?? '-'}
        </p>
        <div className="flex items-center gap-1.5 flex-wrap text-[11px]">
          {order.customerPhone && (
            <a
              href={`tel:${order.customerPhone}`}
              // §9.381 - outside the app, like every other external hand-off. This board is read on
              // a phone too, and a `tel:` in this document unloads it.
              target="_blank"
              rel="noreferrer"
              data-customer-phone
              onClick={(event) => event.stopPropagation()}
              className={cn(
                'flex items-center gap-1 font-medium hover:underline',
                phoneSeen.known ? 'text-emerald-400' : 'text-foreground',
              )}
            >
              <Phone className="w-3 h-3" />
              {order.customerPhone}
              <span
                data-phone-verified={phoneSeen.known ? 'yes' : 'no'}
                className={cn(
                  'text-[9px] font-black px-1.5 py-px rounded-full border',
                  phoneSeen.known
                    ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
                    : 'bg-red-500/15 text-red-400 border-red-500/30',
                )}
              >
                {phoneSeen.label}
              </span>
            </a>
          )}
          {/* §9.154 - legacy shows the address the order was placed from *and* the e-mail it was
            * placed with, each carrying how often that contact has ordered before. */}
          {order.customerEmail && (
            <span
              data-customer-email
              className={cn(
                'flex items-center gap-1 truncate',
                emailSeen.known ? 'text-emerald-400' : 'text-foreground',
              )}
            >
              ✉ {order.customerEmail}
              <span
                data-email-verified={emailSeen.known ? 'yes' : 'no'}
                className={cn(
                  'text-[9px] font-black px-1.5 py-px rounded-full border',
                  emailSeen.known
                    ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
                    : 'bg-red-500/15 text-red-400 border-red-500/30',
                )}
              >
                {emailSeen.label}
              </span>
            </span>
          )}
          {order.customerAddress && (
            // §9.204 — the line a driver needs most, and it was the palest thing on the card. The
            // icon stays muted: it is decoration, the address is the content.
            <span className="flex items-center gap-1 text-foreground truncate" data-customer-address>
              <MapPin className="w-3 h-3 text-muted-foreground" />
              {order.customerAddress}
            </span>
          )}
          {outsideEveryZone(order) && (
            // §9.351 - the server resolved no delivery zone for these coordinates, which is 147 of
            // the owner's orders and 104 of them in Šurany itself. **A marker, not a warning**:
            // §10A.139 accepts every one of them on purpose, so this tells whoever is assigning a
            // driver and does not colour the row or block anything. It sits beside the address
            // because that is the thing it is about.
            <span
              className="flex items-center gap-1 text-[10px] font-medium text-muted-foreground"
              data-outside-zone
              title={tt('Adresa nie je v žiadnej doručovacej zóne - cena dovozu sa neurčila automaticky')}
            >
              <MapPin className="w-3 h-3" />
              <T>mimo zóny</T>
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 flex-wrap mt-0.5">
          {source !== null && (
            <span
              data-order-source={order.source}
              className={cn('text-[10px] font-bold rounded px-1 py-0.5 border leading-none', source.tone)}
            >
              {source.label}
            </span>
          )}
          {payment !== null && (
            <span
              // §9.154 — `unpaid` rather than nothing when the server sent no status: React drops an
              // attribute whose value is undefined, so the badge that says *čaká* on an unpaid
              // CardPay order had no handle at all, and the very case this exists for was untestable.
              data-payment-state={order.paymentStatus ?? 'unpaid'}
              className={cn('text-[10px] font-bold rounded px-1 py-0.5 border leading-none', payment.tone)}
            >
              {payment.label}
            </span>
          )}
          {order.isEdited === true && (
            <span
              data-order-edited
              title={tt('Objednávka bola upravená')}
              className="text-[11px] font-black text-red-500 bg-red-500/15 border border-red-500/30 rounded px-1 py-0.5 leading-none"
            >
              !
            </span>
          )}
        </div>
        {/* §9.154 - a pre-order's promised time and the countdown to it. Three tones for three
          * situations: overdue says by how much, within the hour is urgent, further out is calm. */}
        {promised !== null && (
          <div
            data-scheduled-state={promised.state}
            className={cn(
              'flex items-center gap-1.5 rounded-md px-2 py-1 w-fit text-[11px] mt-0.5 border',
              promised.state === 'late'
                ? 'bg-red-500/15 border-red-500/30 text-red-400'
                : promised.state === 'urgent'
                  ? 'bg-amber-500/15 border-amber-500/30 text-amber-500'
                  : 'bg-amber-500/10 border-amber-500/20 text-amber-400',
            )}
          >
            <CalendarClock className="w-3 h-3 flex-shrink-0" />
            <span className="font-semibold">
              <T>Doručenie:</T>
            </span>
            <span className="font-black">{promised.promisedAt}</span>
            <span className="font-bold tabular-nums">{promised.countdown}</span>
          </div>
        )}

        {/* §9.54 - the note. `/orders` has always carried it and this board never drew it, so a
          * *zvoňte na susedov* reached the kitchen and the driver's own screen and not the person
          * assigning the driver. Nobody typing a note knows who it is for. */}
        {order.notes && (
          <p className="text-[11px] text-muted-foreground truncate" data-order-note title={order.notes}>
            📝 {order.notes}
          </p>
        )}
      </div>

      {/* §9.149 - was `label={order.status}`: the raw key, in grey, on every row. */}
      <StatusBadge color="zinc" label={orderStatusLabel(order.status)} />

      {/* §9.154 - the answer to *what has been waiting longest*, which this board existed to give
        * and could not be asked. Legacy's own format: seconds, then minutes, then `Nh Mm`. */}
      {arrived !== null && (
        <span
          data-order-arrived
          title={`${tt('Prijatá:')} ${order.createdAt ?? ''}`}
          className="flex items-center gap-1 text-[11px] text-muted-foreground whitespace-nowrap"
        >
          <Clock className="w-3 h-3" />
          <span className="font-semibold text-foreground">{arrived}</span>
          {waiting !== null && <span data-order-waiting className="text-muted-foreground/60">({waiting})</span>}
        </span>
      )}

      {/* §9.154 - what the bill was before the discount, which legacy shows and we hid. */}
      <span className="flex items-center gap-1 whitespace-nowrap">
        {discount !== null && (
          <>
            <span data-order-original className="text-xs text-muted-foreground line-through">
              {formatMoney(discount.original)}
            </span>
            <span data-order-discount className="text-xs font-semibold text-amber-400">
              −{formatMoney(discount.discount)}
            </span>
            <span className="text-muted-foreground/60 text-xs">=</span>
          </>
        )}
        <span className="font-bold tabular-nums">{formatMoney(order.total)}</span>
      </span>

      <div className="flex-1" />

      {/* §9.309, owner item B - *"driver assignment on every order feels redundant"*. It was on every
        * card: on an order the kitchen had not started, on one already on the road as a dead disabled
        * control, and on a **counter collection**, which nobody drives. `driverControlFor` decides,
        * and `name` is the branch that keeps this safe — narrowing a control must not hide who has
        * the food. */}
      {/* §9.323 - `'reassign'` shows the name and reveals this picker when asked, so an order
          already on its way does not carry the widest control on the card over its driver's name. */}
      {(driverControl === 'assign' || (driverControl === 'reassign' && reassigning)) && (
        <select
          value={order.driverId ? String(order.driverId) : ''}
          onChange={(event) => {
            const value = Number(event.target.value)
            if (value) onAssignDriver(value)
            setReassigning(false)
          }}
          disabled={driverLocked}
          data-driver-picker={order.orderNumber}
          aria-label={`${tt('Šofér')} #${order.orderNumber}`}
          title={driverLocked ? tt('Objednávku musí vrátiť šofér cez mobilnú aplikáciu.') : undefined}
          className="px-2.5 py-1.5 rounded-lg bg-background border border-border text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <option value="">{tt('- šofér -')}</option>
          {drivers.map((driver) => (
            <option key={driver.id} value={driver.id}>
              {driver.name}
            </option>
          ))}
        </select>
      )}

      {driverControl === 'reassign' && !reassigning && (
        <span
          data-driver-assigned={order.orderNumber}
          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-muted/40 border border-border text-xs font-semibold"
        >
          <UserCheck className="w-3 h-3 text-muted-foreground" />
          <T>{driverName}</T>
          {/* A reveal and not a dialog. The option the owner rejected was *"fast during a rush and
              easy to do by accident"*; a confirmation would trade the second problem for the
              first, so fixing a mistake is still one click away. */}
          <button
            type="button"
            data-driver-change={order.orderNumber}
            onClick={() => setReassigning(true)}
            className="ml-0.5 text-[10px] font-bold uppercase tracking-wide text-primary"
          >
            <T>Zmeniť</T>
          </button>
        </span>
      )}

      {driverControl === 'name' && (
        <span
          data-driver-name={order.orderNumber}
          title={
            driverLocked ? tt('Objednávku musí vrátiť šofér cez mobilnú aplikáciu.') : undefined
          }
          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-muted/40 border border-border text-xs font-semibold"
        >
          <UserCheck className="w-3 h-3 text-muted-foreground" />
          {/* A name from the employee list, not a label - it goes through `T` like every other
            * person's name on this board does. */}
          <T>{driverName}</T>
        </span>
      )}

      {canChangeStatus && mayEditFromBoard(order) && (
        <button
          type="button"
          data-edit-order
          onClick={onEdit}
          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-bold border bg-amber-500/10 text-amber-500 border-amber-500/30 hover:bg-amber-500/20 transition-colors"
        >
          <Pencil className="w-3 h-3" /> <T>Upraviť</T>
        </button>
      )}

      {closesHere && (
        <button
          type="button"
          data-close-in-house
          onClick={() => onStatusChange('delivered')}
          // §9.405 - **blue, and green moved to *Prijať*.** The owner: *"make vydaná as blue
          // colored and Prijať as green color buttons."* It is the right way round on its own
          // merits: an unaccepted web order is the one state on this board where a customer is
          // waiting and nothing is happening, and *Vydaná* is bookkeeping after the food has gone.
          className="px-3 py-1.5 rounded-lg bg-blue-500 text-white text-xs font-bold hover:bg-blue-400 transition-colors"
        >
          <T>Vydaná</T>
        </button>
      )}

      {transitions.map((next) => (
        <button
          key={next}
          type="button"
          // §9.341 - the transition buttons carried no attribute, so nothing could drive a storno
          // from a test. The status the button sets is the one thing a test needs to name.
          data-status-next={next}
          onClick={() => onStatusChange(next)}
          className={cn(
            'flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-bold border transition-colors',
            next === 'cancelled'
              ? 'bg-red-500/10 text-red-500 border-red-500/20 hover:bg-red-500/20'
              : // §9.405 - *✓ Prijať* is the transition to `new`, and it is the one act on this
                // board that takes responsibility for an order a customer is already waiting on.
                // Solid rather than tinted, like *Vydaná*: a tint at 10 % reads as the same button
                // as *→ V príprave* beside it.
                next === 'new'
                ? 'bg-emerald-500 text-white border-emerald-500 hover:bg-emerald-400'
                : 'bg-primary/10 text-primary border-primary/20 hover:bg-primary/20',
          )}
        >
          {next === 'cancelled' && <Ban className="w-3 h-3" />}
          {nextStatusLabel(order.status, next)}
        </button>
      ))}
    </div>
  )
}
